/**
 * RHSocialPage - Module Ressources Humaines / Social
 * Gestion des employés, saisonniers, OFII, MSA, conventions collectives
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle,
  Globe,
  Building,
  Calendar,
  Euro,
  FileSignature,
  ClipboardList,
  Shield,
  Loader2,
  Search,
  Download,
  ExternalLink,
  MapPin,
  Settings,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de contrats
const TYPES_CONTRAT = [
  { value: 'cdi', label: 'CDI' },
  { value: 'cdd', label: 'CDD' },
  { value: 'saisonnier', label: 'Saisonnier' },
  { value: 'interim', label: 'Intérim' },
];

// Qualifications
const QUALIFICATIONS = [
  { value: 'ouvrier', label: 'Ouvrier' },
  { value: 'technicien', label: 'Technicien' },
  { value: 'cadre', label: 'Cadre' },
];

const RHSocialPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [employes, setEmployes] = useState([]);
  const [stats, setStats] = useState({});
  const [alertes, setAlertes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [affichages, setAffichages] = useState([]);
  const [conventions, setConventions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal création employé
  const [showAddEmploye, setShowAddEmploye] = useState(false);
  const [newEmploye, setNewEmploye] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    lieu_naissance: '',
    nationalite: 'Française',
    numero_ss: '',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: '',
    type_contrat: 'saisonnier',
    date_debut: '',
    date_fin: '',
    poste: '',
    qualification: 'ouvrier',
    salaire_horaire: 11.88,
    duree_hebdo: 35,
    est_etranger: false,
    titre_sejour_type: '',
    titre_sejour_numero: '',
    titre_sejour_expiration: '',
    autorisation_travail: false,
    logement_fourni: false,
    logement_adresse: '',
  });

  const token = localStorage.getItem('token');

  // Charger les données
  useEffect(() => {
    fetchDashboard();
    fetchEmployes();
    fetchDocuments();
    fetchAffichages();
    fetchConventions();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rh/stats/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        setAlertes(data.alertes);
      }
    } catch (error) {
      console.error('Erreur dashboard RH:', error);
    }
  };

  const fetchEmployes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/rh/employes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployes(data.employes);
      }
    } catch (error) {
      console.error('Erreur chargement employés:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rh/documents?non_signes=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents);
      }
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    }
  };

  const fetchAffichages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rh/affichages-obligatoires`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAffichages(data.affichages);
      }
    } catch (error) {
      console.error('Erreur chargement affichages:', error);
    }
  };

  const fetchConventions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rh/conventions-collectives`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConventions(data.conventions);
      }
    } catch (error) {
      console.error('Erreur chargement conventions:', error);
    }
  };

  const handleCreateEmploye = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rh/employes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newEmploye)
      });

      if (response.ok) {
        toast.success('Employé créé avec succès !');
        setShowAddEmploye(false);
        fetchEmployes();
        fetchDashboard();
        // Reset form
        setNewEmploye({
          nom: '', prenom: '', date_naissance: '', lieu_naissance: '',
          nationalite: 'Française', numero_ss: '', adresse: '', code_postal: '',
          ville: '', telephone: '', email: '', type_contrat: 'saisonnier',
          date_debut: '', date_fin: '', poste: '', qualification: 'ouvrier',
          salaire_horaire: 11.88, duree_hebdo: 35, est_etranger: false,
          titre_sejour_type: '', titre_sejour_numero: '', titre_sejour_expiration: '',
          autorisation_travail: false, logement_fourni: false, logement_adresse: '',
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur création employé');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleSignDocument = async (docId) => {
    try {
      const response = await fetch(`${API_URL}/api/rh/documents/${docId}/signer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Document signé !');
        fetchDocuments();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Erreur signature');
    }
  };

  // Filtrer les employés
  const filteredEmployes = employes.filter(emp => 
    `${emp.prenom} ${emp.nom}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.poste?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 p-6" data-testid="rh-social-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" />
            Ressources Humaines
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestion des employés, OFII, MSA, conventions collectives
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/agents/config', { state: { selectedAgent: 'rh' } })}
            data-testid="agent-settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Paramètres IA
          </Button>
          <Button onClick={() => setShowAddEmploye(true)} data-testid="add-employe-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Nouvel employé
          </Button>
        </div>
      </div>

      {/* Alertes */}
      {alertes.length > 0 && (
        <div className="space-y-2">
          {alertes.map((alerte, idx) => (
            <div
              key={idx}
              className={`p-3 rounded-lg flex items-center gap-3 ${
                alerte.type === 'danger' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                alerte.type === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
              }`}
            >
              <AlertTriangle className="w-5 h-5" />
              <span>{alerte.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total employés</p>
                <p className="text-2xl font-bold">{stats.total_employes || 0}</p>
              </div>
              <Users className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Actifs</p>
                <p className="text-2xl font-bold text-green-600">{stats.employes_actifs || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Étrangers (OFII)</p>
                <p className="text-2xl font-bold text-blue-600">{stats.employes_etrangers || 0}</p>
              </div>
              <Globe className="w-8 h-8 text-blue-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Docs à signer</p>
                <p className="text-2xl font-bold text-orange-600">{stats.documents_non_signes || 0}</p>
              </div>
              <FileSignature className="w-8 h-8 text-orange-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Heures ce mois</p>
                <p className="text-2xl font-bold">{stats.heures_mois || 0}h</p>
              </div>
              <Clock className="w-8 h-8 text-purple-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="employes">Employés</TabsTrigger>
          <TabsTrigger value="ofii">OFII</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="conventions">Conventions</TabsTrigger>
          <TabsTrigger value="affichages">Affichages</TabsTrigger>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Liens rapides MSA */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  MSA - Déclarations sociales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start" asChild>
                  <a href="https://www.msa.fr/lfy/services-en-ligne" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Accéder au portail MSA
                  </a>
                </Button>
                <p className="text-sm text-muted-foreground">
                  • DPAE (Déclaration Préalable à l'Embauche)<br />
                  • DSN (Déclaration Sociale Nominative)<br />
                  • Bulletins de paie
                </p>
              </CardContent>
            </Card>

            {/* Liens OFII */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  OFII - Travailleurs étrangers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start" asChild>
                  <a href="https://administration-etrangers-en-france.interieur.gouv.fr" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Portail OFII / ANEF
                  </a>
                </Button>
                <p className="text-sm text-muted-foreground">
                  • Déclaration travailleurs saisonniers<br />
                  • Vérification titres de séjour<br />
                  • Autorisations de travail
                </p>
              </CardContent>
            </Card>

            {/* ZRR */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Zone de Revitalisation Rurale (ZRR)
                </CardTitle>
                <CardDescription>
                  Avantages fiscaux et sociaux pour les entreprises en ZRR
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <h4 className="font-semibold text-green-700 dark:text-green-300">Exonération cotisations</h4>
                    <p className="text-sm mt-1">100% pendant 12 mois puis dégressive sur 3 ans pour les CDI</p>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="font-semibold text-blue-700 dark:text-blue-300">Exonération CFE</h4>
                    <p className="text-sm mt-1">Exonération pendant 5 ans pour création d'entreprise</p>
                  </div>
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <h4 className="font-semibold text-purple-700 dark:text-purple-300">Exonération impôts</h4>
                    <p className="text-sm mt-1">100% pendant 5 ans puis dégressive sur 3 ans</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Liste employés */}
        <TabsContent value="employes" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un employé..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredEmployes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground opacity-50" />
                <p className="mt-4 text-muted-foreground">Aucun employé enregistré</p>
                <Button className="mt-4" onClick={() => setShowAddEmploye(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Ajouter le premier employé
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEmployes.map((emp) => (
                <Card key={emp.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{emp.prenom} {emp.nom}</h3>
                        <p className="text-sm text-muted-foreground">{emp.poste}</p>
                      </div>
                      <div className="flex gap-1">
                        {emp.est_etranger && (
                          <Badge variant="outline" className="text-blue-600 border-blue-600">
                            <Globe className="w-3 h-3 mr-1" />
                            OFII
                          </Badge>
                        )}
                        <Badge variant={emp.statut === 'actif' ? 'default' : 'secondary'}>
                          {emp.statut || 'actif'}
                        </Badge>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1 text-sm">
                      <p className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        {TYPES_CONTRAT.find(t => t.value === emp.type_contrat)?.label || emp.type_contrat}
                      </p>
                      <p className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        Depuis le {new Date(emp.date_debut).toLocaleDateString('fr-FR')}
                      </p>
                      <p className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-muted-foreground" />
                        {emp.salaire_horaire}€/h - {emp.duree_hebdo}h/sem
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* OFII */}
        <TabsContent value="ofii" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Employés étrangers - Suivi OFII</CardTitle>
              <CardDescription>
                Gestion des titres de séjour et autorisations de travail
              </CardDescription>
            </CardHeader>
            <CardContent>
              {employes.filter(e => e.est_etranger).length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  Aucun employé étranger enregistré
                </p>
              ) : (
                <div className="space-y-4">
                  {employes.filter(e => e.est_etranger).map((emp) => (
                    <div key={emp.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{emp.prenom} {emp.nom}</h4>
                          <p className="text-sm text-muted-foreground">
                            Nationalité: {emp.nationalite}
                          </p>
                        </div>
                        <div className="text-right">
                          {emp.titre_sejour_expiration && (
                            <Badge variant={
                              new Date(emp.titre_sejour_expiration) < new Date() ? 'destructive' :
                              new Date(emp.titre_sejour_expiration) < new Date(Date.now() + 30*24*60*60*1000) ? 'warning' :
                              'default'
                            }>
                              Expire: {new Date(emp.titre_sejour_expiration).toLocaleDateString('fr-FR')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Type titre:</span>
                          <span className="ml-2">{emp.titre_sejour_type || 'Non renseigné'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">N° titre:</span>
                          <span className="ml-2">{emp.titre_sejour_numero || 'Non renseigné'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Autorisation travail:</span>
                          <span className="ml-2">
                            {emp.autorisation_travail ? (
                              <CheckCircle className="w-4 h-4 inline text-green-600" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 inline text-orange-600" />
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">N° OFII:</span>
                          <span className="ml-2">{emp.ofii_numero || 'Non renseigné'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Documents à faire signer</CardTitle>
              <CardDescription>
                Documents obligatoires en attente de signature
              </CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-600 opacity-50" />
                  Tous les documents sont signés !
                </p>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{doc.titre}</p>
                          <p className="text-sm text-muted-foreground">
                            Employé ID: {doc.employe_id?.slice(0, 8)}...
                          </p>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => handleSignDocument(doc.id)}>
                        <FileSignature className="w-4 h-4 mr-2" />
                        Marquer signé
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Conventions collectives */}
        <TabsContent value="conventions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Conventions Collectives Agricoles</CardTitle>
              <CardDescription>
                Grilles de salaires et règles applicables
              </CardDescription>
            </CardHeader>
            <CardContent>
              {Object.entries(conventions).map(([code, conv]) => (
                <div key={code} className="mb-6 p-4 border rounded-lg">
                  <h4 className="font-semibold text-lg">{conv.nom}</h4>
                  <p className="text-sm text-muted-foreground mb-4">IDCC: {conv.idcc}</p>
                  
                  <h5 className="font-medium mb-2">Salaires minimums horaires:</h5>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(conv.salaire_minimum || {}).map(([niveau, salaire]) => (
                      <div key={niveau} className="p-2 bg-muted/50 rounded">
                        <span className="text-sm capitalize">{niveau.replace('_', ' ')}</span>
                        <span className="float-right font-semibold">{salaire}€/h</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Affichages obligatoires */}
        <TabsContent value="affichages" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Affichages Obligatoires
              </CardTitle>
              <CardDescription>
                Liste des affichages requis dans les locaux de travail
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {affichages.map((affichage, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Shield className="w-5 h-5 text-primary" />
                    <span>{affichage}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Ces affichages doivent être visibles et accessibles à tous les salariés
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal création employé */}
      <Dialog open={showAddEmploye} onOpenChange={setShowAddEmploye}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvel employé</DialogTitle>
            <DialogDescription>
              Remplissez les informations pour créer un nouvel employé
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Informations personnelles */}
            <div>
              <h4 className="font-semibold mb-3">Informations personnelles</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Prénom *</label>
                  <Input
                    value={newEmploye.prenom}
                    onChange={(e) => setNewEmploye({...newEmploye, prenom: e.target.value})}
                    placeholder="Prénom"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Nom *</label>
                  <Input
                    value={newEmploye.nom}
                    onChange={(e) => setNewEmploye({...newEmploye, nom: e.target.value})}
                    placeholder="Nom"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Date de naissance</label>
                  <Input
                    type="date"
                    value={newEmploye.date_naissance}
                    onChange={(e) => setNewEmploye({...newEmploye, date_naissance: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Lieu de naissance</label>
                  <Input
                    value={newEmploye.lieu_naissance}
                    onChange={(e) => setNewEmploye({...newEmploye, lieu_naissance: e.target.value})}
                    placeholder="Ville, Pays"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Nationalité</label>
                  <Input
                    value={newEmploye.nationalite}
                    onChange={(e) => setNewEmploye({...newEmploye, nationalite: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">N° Sécurité Sociale</label>
                  <Input
                    value={newEmploye.numero_ss}
                    onChange={(e) => setNewEmploye({...newEmploye, numero_ss: e.target.value})}
                    placeholder="1 XX XX XX XXX XXX XX"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Téléphone *</label>
                  <Input
                    value={newEmploye.telephone}
                    onChange={(e) => setNewEmploye({...newEmploye, telephone: e.target.value})}
                    placeholder="06 XX XX XX XX"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    value={newEmploye.email}
                    onChange={(e) => setNewEmploye({...newEmploye, email: e.target.value})}
                    placeholder="email@exemple.com"
                  />
                </div>
              </div>
            </div>

            {/* Adresse */}
            <div>
              <h4 className="font-semibold mb-3">Adresse</h4>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  value={newEmploye.adresse}
                  onChange={(e) => setNewEmploye({...newEmploye, adresse: e.target.value})}
                  placeholder="Adresse"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    value={newEmploye.code_postal}
                    onChange={(e) => setNewEmploye({...newEmploye, code_postal: e.target.value})}
                    placeholder="Code postal"
                  />
                  <Input
                    value={newEmploye.ville}
                    onChange={(e) => setNewEmploye({...newEmploye, ville: e.target.value})}
                    placeholder="Ville"
                  />
                </div>
              </div>
            </div>

            {/* Contrat */}
            <div>
              <h4 className="font-semibold mb-3">Contrat de travail</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Type de contrat *</label>
                  <Select
                    value={newEmploye.type_contrat}
                    onValueChange={(v) => setNewEmploye({...newEmploye, type_contrat: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_CONTRAT.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Poste *</label>
                  <Input
                    value={newEmploye.poste}
                    onChange={(e) => setNewEmploye({...newEmploye, poste: e.target.value})}
                    placeholder="Ex: Ouvrier agricole, Tailleur..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Date de début *</label>
                  <Input
                    type="date"
                    value={newEmploye.date_debut}
                    onChange={(e) => setNewEmploye({...newEmploye, date_debut: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Date de fin (CDD/Saisonnier)</label>
                  <Input
                    type="date"
                    value={newEmploye.date_fin}
                    onChange={(e) => setNewEmploye({...newEmploye, date_fin: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Salaire horaire (€) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newEmploye.salaire_horaire}
                    onChange={(e) => setNewEmploye({...newEmploye, salaire_horaire: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Heures/semaine</label>
                  <Input
                    type="number"
                    value={newEmploye.duree_hebdo}
                    onChange={(e) => setNewEmploye({...newEmploye, duree_hebdo: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
            </div>

            {/* Travailleur étranger */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Travailleur étranger (OFII)
              </h4>
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="est_etranger"
                  checked={newEmploye.est_etranger}
                  onChange={(e) => setNewEmploye({...newEmploye, est_etranger: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="est_etranger">Cet employé est un travailleur étranger</label>
              </div>

              {newEmploye.est_etranger && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div>
                    <label className="text-sm font-medium">Type de titre de séjour</label>
                    <Input
                      value={newEmploye.titre_sejour_type}
                      onChange={(e) => setNewEmploye({...newEmploye, titre_sejour_type: e.target.value})}
                      placeholder="Ex: Carte de séjour temporaire"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">N° titre de séjour</label>
                    <Input
                      value={newEmploye.titre_sejour_numero}
                      onChange={(e) => setNewEmploye({...newEmploye, titre_sejour_numero: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Date d'expiration</label>
                    <Input
                      type="date"
                      value={newEmploye.titre_sejour_expiration}
                      onChange={(e) => setNewEmploye({...newEmploye, titre_sejour_expiration: e.target.value})}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="autorisation_travail"
                      checked={newEmploye.autorisation_travail}
                      onChange={(e) => setNewEmploye({...newEmploye, autorisation_travail: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="autorisation_travail">Autorisation de travail vérifiée</label>
                  </div>
                </div>
              )}
            </div>

            {/* Logement */}
            <div>
              <h4 className="font-semibold mb-3">Logement (saisonniers)</h4>
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="logement_fourni"
                  checked={newEmploye.logement_fourni}
                  onChange={(e) => setNewEmploye({...newEmploye, logement_fourni: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="logement_fourni">Logement fourni par l'employeur</label>
              </div>
              {newEmploye.logement_fourni && (
                <Input
                  value={newEmploye.logement_adresse}
                  onChange={(e) => setNewEmploye({...newEmploye, logement_adresse: e.target.value})}
                  placeholder="Adresse du logement"
                />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowAddEmploye(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateEmploye}>
              <UserPlus className="w-4 h-4 mr-2" />
              Créer l'employé
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RHSocialPage;
